import { useId, type ReactNode } from 'react'
import { cn } from '@/lib/format'

export interface RadioCardOption<V extends string> {
  value: V
  label: ReactNode
  description?: ReactNode
  disabled?: boolean
}

export interface RadioCardsProps<V extends string> {
  /** Group label (visible). */
  legend: ReactNode
  /** Hide the legend visually but keep it for screen readers. */
  legendHidden?: boolean
  name?: string
  value: V
  options: RadioCardOption<V>[]
  onChange: (value: V) => void
  disabled?: boolean
  /** Lay out side by side from sm up (default stacked). */
  columns?: 1 | 2 | 3
  className?: string
}

/**
 * A radio group drawn as selectable cards. Real <input type="radio">s, so
 * arrow keys, Tab and screen readers work as usual.
 */
export function RadioCards<V extends string>({
  legend,
  legendHidden,
  name,
  value,
  options,
  onChange,
  disabled,
  columns = 1,
  className,
}: RadioCardsProps<V>) {
  const generated = useId()
  const group = name ?? generated
  return (
    <fieldset className={cn('grid min-w-0 gap-2', className)} disabled={disabled}>
      <legend className={cn('mb-2 text-xs font-semibold text-ink', legendHidden && 'sr-only')}>{legend}</legend>
      <div className={cn('grid gap-2', columns === 2 && 'sm:grid-cols-2', columns === 3 && 'sm:grid-cols-3')}>
        {options.map((o) => {
          const checked = o.value === value
          return (
            <label
              key={o.value}
              className={cn(
                'relative flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2.5 text-[13px] transition-colors',
                'has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent',
                checked ? 'border-accent/60 bg-accent-soft/50' : 'border-line bg-raised/40 hover:border-line-strong',
                (disabled || o.disabled) && 'cursor-not-allowed opacity-60',
              )}
            >
              <input
                type="radio"
                name={group}
                value={o.value}
                checked={checked}
                disabled={disabled || o.disabled}
                onChange={() => onChange(o.value)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[rgb(var(--accent-solid))]"
              />
              <span className="grid min-w-0 gap-0.5">
                <span className="font-semibold text-ink">{o.label}</span>
                {o.description ? <span className="text-xs text-muted">{o.description}</span> : null}
              </span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}
